import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import { Fuel, Users } from "lucide-react";

const BON_ARTICLES = [
  {
    t: "Article 1 — Objet",
    p: [
      "Le présent programme a pour objet d'offrir un bon carburant d'une valeur de 25 € à tout élève ayant réussi son permis de conduire en passant par la plateforme Plan Permis pour trouver une auto-école partenaire.",
    ],
  },
  {
    t: "Article 2 — Éligibilité",
    p: ["Pour bénéficier du bon carburant, l'élève doit remplir toutes les conditions suivantes :"],
    li: [
      "Avoir utilisé la plateforme Plan Permis pour trouver une auto-école partenaire (permis accéléré, transfert de dossier ou examen en campagne).",
      "Avoir réussi l'examen du permis de conduire.",
      "Avoir contacté la plateforme pour demander le bon.",
      "Fournir une preuve de réussite (photo du permis ou relevé de notes).",
      "Ne jamais avoir reçu de bon carburant de la part de Plan Permis auparavant.",
    ],
  },
  {
    t: "Article 3 — Une seule fois par personne",
    p: ["Le bon carburant est accordé une seule fois par personne, à vie. L'identification de la personne se fait par son adresse email, son numéro de téléphone et ses nom, prénom et date de naissance. Si l'une de ces informations correspond à une personne ayant déjà reçu un bon, la demande sera automatiquement refusée."],
  },
  {
    t: "Article 4 — Cas particulier : échec puis réussite",
    p: ["Si un élève échoue une première fois, il ne reçoit pas de bon. S'il revient sur la plateforme pour une nouvelle formation et réussit ensuite, il pourra recevoir son bon, à condition de ne jamais l'avoir reçu auparavant. Le fait d'avoir échoué puis réussi ne donne pas droit à deux bons."],
  },
  {
    t: "Article 5 — Procédure de demande",
    p: ["L'élève remplit le formulaire en ligne (nom, prénom, email, téléphone, date de naissance, auto-école fréquentée, date de réussite) et joint une preuve de réussite. La plateforme vérifie auprès de l'auto-école que la réussite est confirmée. Si toutes les conditions sont remplies, le bon est validé et envoyé, et l'élève reçoit une confirmation par email."],
  },
  {
    t: "Article 6 — Délai de traitement",
    p: ["Le traitement de la demande prend en moyenne 7 à 15 jours ouvrables à compter de la réception de la demande et de la confirmation de l'auto-école."],
  },
  {
    t: "Article 7 — Refus",
    p: ["La demande peut être refusée si :"],
    li: [
      "L'élève a déjà reçu un bon carburant.",
      "La réussite n'est pas confirmée par l'auto-école.",
      "Les informations fournies sont fausses ou incomplètes.",
      "L'élève n'a pas utilisé la plateforme pour trouver son auto-école.",
    ],
    after: "En cas de refus, l'élève en est informé par email avec le motif.",
  },
  {
    t: "Article 8 — Fraude",
    p: ["Toute tentative de fraude (fausse déclaration, usurpation d'identité, documents falsifiés) entraîne l'annulation définitive du droit au bon et pourra faire l'objet de poursuites."],
  },
  {
    t: "Article 9 — Données personnelles",
    p: ["Les informations collectées sont nécessaires à la gestion du programme. Elles sont conservées pendant une durée maximale de 3 ans après la dernière interaction. Conformément au RGPD, l'élève dispose d'un droit d'accès, de rectification et de suppression de ses données."],
  },
];

const RECO_ARTICLES = [
  {
    t: "Article 1 — Objet",
    p: ["Le programme de recommandation permet à un parrain (particulier ou professionnel) de recevoir une commission pour chaque nouveau candidat qu'il apporte à la plateforme et qui s'inscrit réellement dans une auto-école partenaire."],
  },
  {
    t: "Article 2 — Montant de la commission",
    p: ["La commission est un montant FIXE selon le type de parrain :"],
    li: [
      "Parrain particulier : 25 € par filleul qualifié.",
      "Parrain professionnel (auto-école, formateur) : 50 € par filleul qualifié, selon les conditions du contrat professionnel.",
    ],
  },
  {
    t: "Article 3 — Conditions pour recevoir la commission",
    p: ["Pour qu'une commission soit versée au parrain, toutes les conditions suivantes doivent être remplies :"],
    li: [
      "Nouveauté du filleul : il ne doit jamais avoir été enregistré sur la plateforme auparavant (même email, téléphone ou identité).",
      "Code valide : le filleul doit avoir utilisé le code de parrainage du parrain lors de sa demande.",
      "Inscription confirmée : le filleul doit s'être inscrit dans une auto-école partenaire.",
      "Délai de rétractation : le délai légal de 14 jours doit être écoulé depuis l'inscription confirmée.",
      "Pas de doublon : le filleul ne doit pas être déjà rattaché à un autre parrain.",
    ],
  },
  {
    t: "Article 4 — Interdiction de parrainer deux fois la même personne",
    p: ["Un parrain ne peut pas parrainer deux fois la même personne. Si un filleul est déjà enregistré dans la base, il ne peut pas être parrainé à nouveau : un parrain ne touchera jamais deux commissions pour un même filleul."],
  },
  {
    t: "Article 5 — Auto-parrainage interdit",
    p: ["Un parrain ne peut pas se parrainer lui-même. Si l'email ou le téléphone du filleul est identique à celui du parrain, la commission est automatiquement annulée."],
  },
  {
    t: "Article 6 — Parrainage familial",
    p: ["Si le parrain et le filleul partagent la même adresse ou un lien de parenté direct (même foyer, conjoint, PACS), la commission du parrain est maintenue. Le parrain doit déclarer ce lien lors de l'inscription du filleul."],
  },
  {
    t: "Article 7 — Statut de la commission",
    p: ["La commission est créée avec le statut « en attente », puis passe à « validée » après vérification, puis « à payer », puis « payée ». En cas de rétractation, d'annulation ou de non-paiement par l'auto-école, la commission est annulée ou récupérée."],
  },
  {
    t: "Article 8 — Plafond mensuel",
    p: ["Un parrain particulier peut parrainer jusqu'à 10 filleuls rémunérés par mois. Au-delà, les commissions ne sont plus versées pour le mois en cours. Les parrains professionnels ne sont pas soumis à ce plafond."],
  },
  {
    t: "Article 9 — Fraude",
    p: ["Toute tentative de fraude (faux comptes, auto-parrainage, usurpation, doublons) entraîne l'annulation des commissions et la suspension définitive du compte parrain."],
  },
  {
    t: "Article 10 — Modification des conditions",
    p: ["La plateforme se réserve le droit de modifier les présentes conditions à tout moment. Les parrains seront informés par email."],
  },
];

const RECAP = [
  { k: "Bénéficiaire", bon: "Élève qui réussit", reco: "Parrain" },
  { k: "Montant", bon: "25 €", reco: "25 € (particulier) / 50 € (pro)" },
  { k: "Déclencheur", bon: "Réussite confirmée", reco: "Inscription validée + 14 j" },
  { k: "Fréquence", bon: "1 fois par personne", reco: "À chaque nouveau filleul" },
  { k: "Anti-doublon", bon: "Email + téléphone uniques", reco: "Filleul nouveau uniquement" },
  { k: "Cumul", bon: "Cumulable avec la recommandation", reco: "Cumulable avec le bon carburant" },
];

function Article({ a }) {
  return (
    <article className="border-t border-neutral-200 pt-fib-4">
      <h3 className="text-gr-lg font-bold text-neutral-900">{a.t}</h3>
      {a.p.map((para) => (
        <p key={para} className="mt-fib-2 text-gr-base leading-golden text-neutral-600">{para}</p>
      ))}
      {a.li && (
        <ul className="mt-fib-3 list-disc space-y-fib-1 pl-5 text-gr-base text-neutral-600">
          {a.li.map((item) => <li key={item}>{item}</li>)}
        </ul>
      )}
      {a.after && <p className="mt-fib-3 text-gr-base text-neutral-600">{a.after}</p>}
    </article>
  );
}

export default function ConditionsRecompenses() {
  useSeo({
    title: "Conditions — Bon carburant 25 € & Recommandation | Plan Permis",
    description:
      "Conditions générales du bon carburant de 25 € (élève qui réussit) et du programme de recommandation (parrain particulier 25 € / professionnel 50 €).",
    path: "/conditions-recompenses",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Combien de fois puis-je recevoir le bon carburant de 25 € ?",
            acceptedAnswer: { "@type": "Answer", text: "Une seule fois par personne, à vie. L'identification se fait par email, téléphone et par nom, prénom et date de naissance." },
          },
          {
            "@type": "Question",
            name: "Combien gagne un parrain avec le programme de recommandation ?",
            acceptedAnswer: { "@type": "Answer", text: "25 € par filleul qualifié pour un particulier (plafond 10 filleuls par mois) et 50 € par filleul pour un professionnel." },
          },
          {
            "@type": "Question",
            name: "Le bon carburant est-il cumulable avec la recommandation ?",
            acceptedAnswer: { "@type": "Answer", text: "Oui, le bon carburant (élève) et la commission de recommandation (parrain) sont cumulables." },
          },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="flex items-center justify-between px-6 py-fib-4 sm:px-12">
        <Link to="/" data-testid="conditions-logo-link"><Logo className="h-12" /></Link>
        <div className="flex items-center gap-fib-4 text-gr-sm font-semibold text-neutral-600">
          <Link to="/bon-carburant" className="transition-colors hover:text-brand">Bon carburant</Link>
          <Link to="/ambassadeur" className="transition-colors hover:text-brand">Ambassadeurs</Link>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="bg-neutral-900 px-6 py-fib-7 text-white sm:px-12">
          <div className="mx-auto max-w-3xl">
            <span className="text-gr-xs font-bold uppercase tracking-[0.2em] text-white/60">Conditions générales</span>
            <h1 className="mt-fib-3 text-balance text-[2.618rem] font-black leading-[1.05] tracking-tight sm:text-gr-2xl" data-testid="conditions-title">
              Récompenses Plan Permis&nbsp;: bon carburant &amp; recommandation
            </h1>
            <p className="mt-fib-4 measure-wide text-gr-lg text-white/80">
              Les conditions applicables au bon carburant de 25 € (pour l'élève qui réussit) et au programme de
              recommandation (pour le parrain). Les deux récompenses sont cumulables.
            </p>
          </div>
        </section>

        {/* Récapitulatif */}
        <section aria-labelledby="recap-title" className="px-6 py-fib-6 sm:px-12">
          <div className="mx-auto max-w-4xl">
            <h2 id="recap-title" className="text-gr-2xl font-extrabold tracking-tight text-neutral-900">En un coup d’œil</h2>
            <div className="mt-fib-5 overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
              <table className="w-full text-gr-sm" data-testid="conditions-recap-table">
                <thead className="bg-neutral-50 text-left text-gr-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="px-fib-4 py-fib-3"></th>
                    <th className="px-fib-4 py-fib-3"><span className="inline-flex items-center gap-fib-2 text-ink"><Fuel className="h-4 w-4" /> Bon carburant</span></th>
                    <th className="px-fib-4 py-fib-3"><span className="inline-flex items-center gap-fib-2 text-brand"><Users className="h-4 w-4" /> Recommandation</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {RECAP.map((r) => (
                    <tr key={r.k}>
                      <td className="px-fib-4 py-fib-3 font-semibold text-neutral-700">{r.k}</td>
                      <td className="px-fib-4 py-fib-3 text-neutral-600">{r.bon}</td>
                      <td className="px-fib-4 py-fib-3 text-neutral-600">{r.reco}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Partie 1 — Bon carburant */}
        <section aria-labelledby="bon-title" className="bg-white px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-3xl">
            <h2 id="bon-title" className="flex items-center gap-fib-3 text-gr-2xl font-extrabold tracking-tight text-neutral-900">
              <Fuel className="h-7 w-7 text-brand" /> Partie 1 — Bon carburant (25 €)
            </h2>
            <div className="mt-fib-5 space-y-fib-4">
              {BON_ARTICLES.map((a) => <Article key={a.t} a={a} />)}
            </div>
          </div>
        </section>

        {/* Partie 2 — Recommandation */}
        <section aria-labelledby="reco-title" className="px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-3xl">
            <h2 id="reco-title" className="flex items-center gap-fib-3 text-gr-2xl font-extrabold tracking-tight text-neutral-900">
              <Users className="h-7 w-7 text-brand" /> Partie 2 — Recommandation (parrain)
            </h2>
            <div className="mt-fib-5 space-y-fib-4">
              {RECO_ARTICLES.map((a) => <Article key={a.t} a={a} />)}
            </div>
            <div className="mt-fib-6 flex flex-wrap gap-fib-3">
              <Link to="/bon-carburant" className="rounded-full bg-brand px-fib-5 py-fib-3 text-gr-sm font-bold text-white transition-colors hover:bg-brand-hover">
                Demander mon bon carburant
              </Link>
              <Link to="/ambassadeur" className="rounded-full bg-ink px-fib-5 py-fib-3 text-gr-sm font-bold text-white transition-colors hover:bg-ink-700">
                Devenir ambassadeur
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
